import { useRef, useState, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { PIShell, Drill, PIInsight, PracticeLadder } from "@/components/pi/PIShell";
import { PianoDiagram } from "@/components/PianoDiagram";
import { LessonMidi } from "@/components/MidiPlayer";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio } from "@/audio/piano";
import { playRhythm, RhythmHandle } from "@/audio/rhythm";

interface Subdivision { n: number; word: string; syllables: string[]; description: string; }
const SUBDIVISIONS: Subdivision[] = [
  { n: 1, word: "Ta",         syllables: ["Ta"],                              description: "Just the beat. No subdivision." },
  { n: 2, word: "Taka",       syllables: ["Ta", "ka"],                        description: "Eighth notes. Two attacks per beat." },
  { n: 3, word: "Takita",     syllables: ["Ta", "ki", "ta"],                  description: "Triplet. Three attacks per beat." },
  { n: 4, word: "Takadimi",   syllables: ["Ta", "ka", "di", "mi"],            description: "Sixteenth notes. Four attacks per beat." },
  { n: 5, word: "Takatakita", syllables: ["Ta", "ka", "ta", "ki", "ta"],      description: "Quintuplet. Five per beat (2 + 3)." },
];

export default function PianoIntermediate17() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "17");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/piano-intermediate/PI17-Konnakol-Animated.mp4"
        caption={`Three Carnatic syllables — Taka (÷2) · Takita (÷3) · Takadimi (÷4) — one beat, three slicings`}
        hint={`≈ 22 sec · say it, then play it`}
      />
      <KeyProvider defaultTonic="C" availableQualities={["major"]}>
        <PIShell
          title="Building Rhythmic Phrases — Konnakol"
          exerciseGoal="Five Konnakol syllable groups split a beat 1, 2, 3, 4 or 5 ways. Mix them across a bar — that's a rhythm phrase. The body learns it before the keyboard does."
          bpm={88}
          difficulty="Intermediate"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);
  const [activeIdx, setActiveIdx] = useState(3);
  const [running, setRunning] = useState(false);
  const [step, setStep] = useState(-1);
  const handleRef = useRef<RhythmHandle | null>(null);
  const sub = SUBDIVISIONS[activeIdx];

  const stop = () => { handleRef.current?.stop(); handleRef.current = null; setRunning(false); setStep(-1); };
  const play = async () => {
    kickAudio();
    stop();
    setRunning(true);
    const totalSteps = 4 * sub.n;
    const events = Array.from({ length: totalSteps }, (_, s) => ({
      beat: s,
      notes: [midiToPitchName(tonicMidi, k.useFlats)],
      durationSteps: 1,
      velocity: s % sub.n === 0 ? 0.9 : 0.55,
    }));
    handleRef.current = await playRhythm(events, {
      bpm: 88, subdivide: sub.n, totalSteps, loop: false, click: true, beatsPerBar: 4,
      onStep: (s) => setStep(s),
    });
    const totalSec = (totalSteps / sub.n) * (60 / 88);
    window.setTimeout(stop, Math.ceil(totalSec * 1000) + 200);
  };

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram highlights={[{ pc: k.tonicPc, tone: "root", label: "Sa" }]} octaves={2} startOctave={3} playable />
        <p className="mt-2 text-[12.5px] text-cyan-300/70">
          Konnakol is taught with the body. The atomizer below is the lesson; the keyboard is just here so
          you can pin a tonic if you want to vocalise the pulse on a pitch.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The big idea" title="A spoken language for rhythm">
          <p>
            <strong>Konnakol</strong> is the South Indian rhythmic-syllable system, evolved from the
            Mridangam drum. The genius: every way of subdividing a beat has its own monosyllabic
            word. The mind doesn't have to count — it just speaks. Speech gets into the body faster
            than counting ever does.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "The mind can control all the divisions, the mind can control groups of notes and
            whatever you do on the piano, whether it's arpeggiation, whether it's following someone
            else like a drummer and trying to get by, Konnakol is a very nice tool to help you out
            with accents."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="The atomizer" title="Click 1, 2, 3, 4, 5 — see the beat split">
          <KonnakolAtomizer subdivisions={SUBDIVISIONS} activeIdx={activeIdx} step={running ? step : -1} onPick={(i) => { stop(); setActiveIdx(i); }} />
          <p className="mt-2 text-[14px] text-cyan-50/95">
            <strong>{sub.word}</strong> · <em>{sub.description}</em>
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={play} disabled={running} className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300 disabled:opacity-50">▶ Speak {sub.word} for 4 beats</button>
            {running && <button onClick={stop} className="rounded-lg border border-rose-400/60 bg-rose-500/10 px-3 py-2 text-sm font-semibold text-rose-200 hover:bg-rose-500/20">■ Stop</button>}
          </div>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="Mix &amp; match" title="A rhythm phrase = a sequence of subdivisions">
          <p>
            Once each subdivision feels natural, mix them. A bar that goes <strong>Takadimi · Takadimi
            · Takita · Ta</strong> is <em>4 + 4 + 3 + 1 = 12 attacks</em> across four beats. Different
            subdivisions per beat = different rhythmic phrases.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "So now each of these are individual phrases. What can you do with that? Well, the sky
            is literally probably not even the limit if you think about it because it's numbers."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="Body before keyboard" title="Snap, clap, speak">
          <p>
            Practice this <strong>without the piano first</strong>. Snap on every beat with one hand.
            Speak the Konnakol word with each snap. Switch words mid-bar without breaking the snap.
            The piano is the second step — once your body has the phrase, your fingers will reproduce
            it almost effortlessly.
          </p>
          <PIInsight>
            <p>
              Konnakol grew out of the mridangam: whatever you speak with your voice starts
              developing phrases and accents, and that is very easy on the mind. Your hands then
              react to what the voice has already decided.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="5" label="Apply on the piano" title="One pitch, many rhythms">
          <p>
            On the keyboard, take a single note (the tonic) and play it in different subdivisions
            across a bar. <strong>Ta</strong> on beat 1, <strong>Taka</strong> on beat 2,
            <strong> Takita</strong> on beat 3, <strong>Takadimi</strong> on beat 4. Then try Jason's
            own phrase, <strong>Takadimi · Takadimi · Takita · Ta</strong>, before adding any other
            pitches.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="6" label="Play along" title="The Konnakol phrase loop">
          <LessonMidi courseSlug="piano-intermediate" order="17" label={`Konnakol phrase · ${k.tonicName}`} originalBpm={88} />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="7" label="Practice ladder" title="Twelve minutes, four rungs">
          <PracticeLadder
            intro={<>Speak before you play. Your body is the metronome.</>}
            rungs={[
              { title: "Ta · Taka",                  minutes: 3, accent: "amber",   detail: "1 and 2 subdivisions only. Snap on every beat. Speak Ta on beat 1, Taka on beat 2, alternate." },
              { title: "Add Takita (triplets)",      minutes: 3, accent: "cyan",    detail: "Triplet feel. Takita is the gateway to swing. Speak twelve attacks across four beats." },
              { title: "Add Takadimi",                minutes: 3, accent: "rose",    detail: "Now sixteenths join. Speak a bar that mixes Takadimi with Takita." },
              { title: "Free phrasing",               minutes: 3, accent: "emerald", detail: "Compose a four-beat phrase using any subdivisions. Speak it. Then play it on the tonic. Then on a scale." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

function KonnakolAtomizer({
  subdivisions, activeIdx, step, onPick,
}: { subdivisions: Subdivision[]; activeIdx: number; step: number; onPick: (i: number) => void; }) {
  return (
    <div className="my-4 rounded-2xl border border-cyan-400/30 bg-zinc-900/40 p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-300">⊟ Atomizer · split a beat</span>
        {subdivisions.map((s, i) => (
          <button key={s.n} onClick={() => onPick(i)} className={`rounded-md border px-2.5 py-1 font-mono text-[12px] font-bold ${
            i === activeIdx ? "border-amber-400 bg-amber-500/15 text-amber-200" : "border-cyan-400/30 bg-cyan-500/5 text-cyan-200 hover:bg-cyan-500/15"
          }`}>{s.n} · {s.word}</button>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-2">
        {[0, 1, 2, 3].map((beat) => {
          const sub = subdivisions[activeIdx];
          return (
            <div key={beat} className="rounded-lg border border-cyan-400/20 bg-zinc-900/60 p-2">
              <div className="mb-1 text-center font-mono text-[10px] uppercase tracking-wider text-cyan-300/70">beat {beat + 1}</div>
              <div className="flex gap-0.5">
                {sub.syllables.map((syl, j) => {
                  const stepIdx = beat * sub.n + j;
                  const active = step === stepIdx;
                  const isDownbeat = j === 0;
                  return (
                    <motion.div key={j} animate={{ scale: active ? 1.18 : 1, boxShadow: active ? "0 0 0 2px #FCD34D" : "0 0 0 0 transparent" }} transition={{ duration: 0.1 }}
                      className={`flex flex-1 items-center justify-center rounded py-2 text-[12px] font-bold ${isDownbeat ? "bg-amber-400 text-zinc-900" : "bg-cyan-500 text-white"}`}>
                      {syl}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)]">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}
function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (<motion.div initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: "easeOut" }}>{children}</motion.div>);
}
function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote className="text-[17px] leading-relaxed text-cyan-50/95" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>
        {children}
      </blockquote>
      {attribution && <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">— {attribution}</figcaption>}
    </figure>
  );
}
